import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Shield, Users, BookOpen, ArrowLeftRight, Flag, EyeOff, Eye, CheckCircle2, HeartHandshake, Mail, Trash2, Loader2, MapPin, Star, Inbox, Send } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const STATUS_LABEL = {
  available: "Disponibile", in_negotiation: "In trattativa", exchanged: "Scambiato",
  suspended: "Sospeso", pending: "In attesa", accepted: "Accettata", rejected: "Rifiutata", completed: "Completata",
};
const statusClass = (s) => ({
  available: "bg-emerald-100 text-emerald-800 border-emerald-300",
  in_negotiation: "bg-amber-100 text-amber-800 border-amber-300",
  accepted: "bg-amber-100 text-amber-800 border-amber-300",
  pending: "bg-sky-100 text-sky-800 border-sky-300",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  exchanged: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-rose-100 text-rose-800 border-rose-300",
  suspended: "bg-stone-200 text-stone-700 border-stone-300",
}[s] || "bg-muted text-muted-foreground");

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2"><Icon className="w-4 h-4" />{label}</div>
      <p className="text-3xl font-serif font-bold">{value}</p>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [donations, setDonations] = useState([]);
  const [users, setUsers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const [s, r, d, u] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/reports"), api.get("/admin/donations"), api.get("/admin/users"),
    ]);
    setStats(s.data); setReports(r.data); setDonations(d.data); setUsers(u.data);
  }, []);

  const openDetail = async (uid) => {
    setDetailOpen(true); setDetail(null); setDetailLoading(true);
    try { const { data } = await api.get(`/admin/users/${uid}/detail`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setDetailOpen(false); }
    finally { setDetailLoading(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(`Utente ${deleteTarget.name} eliminato`);
      setDeleteTarget(null); setDetailOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  useEffect(() => { load(); }, [load]);

  const handleDonation = async (id) => {
    try { await api.patch(`/admin/donations/${id}/handle`); toast.success("Donazione segnata come gestita"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleHide = async (bookId, hidden) => {
    try { await api.patch(`/admin/books/${bookId}/hide`, { hidden }); toast.success(hidden ? "Annuncio nascosto" : "Annuncio ripristinato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resolve = async (reportId) => {
    try { await api.patch(`/admin/reports/${reportId}/resolve`); toast.success("Segnalazione risolta"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purging, setPurging] = useState(false);
  const purgeDemo = async () => {
    setPurging(true);
    try {
      const { data } = await api.delete("/admin/demo-books");
      toast.success(data.deleted > 0 ? `${data.deleted} libri di esempio rimossi` : "Nessun libro di esempio da rimuovere");
      setPurgeOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPurging(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-start sm:items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><Shield className="w-6 h-6" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold tracking-tight">Amministrazione</h1>
            <p className="text-sm text-muted-foreground">Gestione segnalazioni e moderazione annunci.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" data-testid="purge-demo-button" onClick={() => setPurgeOpen(true)} className="text-destructive hover:text-destructive">
          <Trash2 className="w-4 h-4 mr-2" />Rimuovi libri di esempio
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={Users} label="Utenti" value={stats.users} />
          <Stat icon={BookOpen} label="Libri reali" value={stats.books} />
          <Stat icon={ArrowLeftRight} label="Proposte" value={stats.proposals} />
          <Stat icon={Flag} label="Segnalazioni aperte" value={stats.open_reports} />
        </div>
      )}

      <h2 className="text-xl font-serif font-semibold mb-4 flex items-center gap-2" data-testid="users-section"><Users className="w-5 h-5" />Utenti iscritti <span className="text-sm font-normal text-muted-foreground">({users.length})</span></h2>
      {users.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-border text-muted-foreground">Nessun utente iscritto.</div>
      ) : (
        <div className="mb-10 rounded-2xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Nome</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Data iscrizione</th>
                  <th className="px-4 py-3 font-medium text-center whitespace-nowrap">Prestiti attivi</th>
                  <th className="px-4 py-3 font-medium text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} onClick={() => openDetail(u.id)} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium hover:text-primary">{u.name}</span>
                        {u.role === "admin" && <Badge className="bg-primary text-primary-foreground text-[10px]">Admin</Badge>}
                      </div>
                      {u.comune && <span className="text-xs text-muted-foreground">{u.comune}</span>}
                    </td>
                    <td className="px-4 py-3"><a href={`mailto:${u.email}`} onClick={(e) => e.stopPropagation()} className="text-muted-foreground hover:text-primary break-all">{u.email}</a></td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                    <td className="px-4 py-3 text-center">
                      {u.active_loans > 0 ? (
                        <Badge className="bg-secondary text-secondary-foreground" data-testid={`user-loans-${u.id}`}>{u.active_loans}</Badge>
                      ) : (
                        <span className="text-muted-foreground" data-testid={`user-loans-${u.id}`}>0</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="outline" data-testid={`user-detail-${u.id}`} onClick={(e) => { e.stopPropagation(); openDetail(u.id); }}>Dettagli</Button>
                        {u.role !== "admin" && (
                          <Button size="sm" variant="outline" data-testid={`user-delete-${u.id}`} onClick={(e) => { e.stopPropagation(); setDeleteTarget(u); }} className="text-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h2 className="text-xl font-serif font-semibold mb-4 flex items-center gap-2"><Flag className="w-5 h-5" />Segnalazioni</h2>
      {reports.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-border text-muted-foreground">Nessuna segnalazione ricevuta.</div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} data-testid={`report-${r.id}`} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="capitalize">{r.target_type === "book" ? "Annuncio" : "Utente"}</Badge>
                    <Badge variant="outline" className={r.status === "open" ? "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300" : "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300"}>{r.status === "open" ? "Aperta" : "Risolta"}</Badge>
                    <span className="text-xs text-muted-foreground">da {r.reporter_name} · {new Date(r.created_at).toLocaleDateString("it-IT")}</span>
                  </div>
                  {r.target_type === "book" && r.target ? (
                    <div className="flex items-center gap-3 mt-2">
                      <div className="w-10 h-14 rounded bg-muted overflow-hidden shrink-0">{fileUrl(r.target) && <img src={fileUrl(r.target)} alt="" className="w-full h-full object-cover" />}</div>
                      <div>
                        <Link to={`/libro/${r.target.id}`} className="font-medium hover:text-primary">{r.target.title}</Link>
                        {r.target.is_hidden && <Badge className="ml-2 bg-stone-800 text-white">Nascosto</Badge>}
                      </div>
                    </div>
                  ) : r.target ? <p className="text-sm mt-1">Utente: <strong>{r.target.name}</strong></p> : <p className="text-sm text-muted-foreground mt-1">(elemento rimosso)</p>}
                  <p className="text-sm mt-2 rounded-lg bg-muted p-2.5">"{r.reason}"</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {r.target_type === "book" && r.target && (
                  r.target.is_hidden ? (
                    <Button size="sm" variant="outline" data-testid={`unhide-${r.id}`} onClick={() => toggleHide(r.target.id, false)}><Eye className="w-4 h-4 mr-2" />Ripristina annuncio</Button>
                  ) : (
                    <Button size="sm" variant="outline" data-testid={`hide-${r.id}`} onClick={() => toggleHide(r.target.id, true)} className="text-destructive"><EyeOff className="w-4 h-4 mr-2" />Nascondi annuncio</Button>
                  )
                )}
                {r.status === "open" && <Button size="sm" data-testid={`resolve-${r.id}`} onClick={() => resolve(r.id)} className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"><CheckCircle2 className="w-4 h-4 mr-2" />Segna risolta</Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="text-xl font-serif font-semibold mb-4 mt-10 flex items-center gap-2"><HeartHandshake className="w-5 h-5" />Donazioni ricevute</h2>
      {donations.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-border text-muted-foreground">Nessuna donazione ricevuta.</div>
      ) : (
        <div className="space-y-3">
          {donations.map((d) => (
            <div key={d.id} data-testid={`donation-${d.id}`} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-medium">{d.name}</span>
                    <Badge variant="outline" className={d.status === "new" ? "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300" : "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300"}>{d.status === "new" ? "Da gestire" : "Gestita"}</Badge>
                    <span className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString("it-IT")}</span>
                  </div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /><a href={`mailto:${d.email}`} className="hover:text-primary">{d.email}</a></p>
                  <p className="text-sm mt-1"><strong>~{d.book_count}</strong> libri da donare</p>
                  {d.note && <p className="text-sm mt-2 rounded-lg bg-muted p-2.5">"{d.note}"</p>}
                </div>
                {d.status === "new" && (
                  <Button size="sm" data-testid={`handle-donation-${d.id}`} onClick={() => handleDonation(d.id)} className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"><CheckCircle2 className="w-4 h-4 mr-2" />Segna gestita</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="user-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2"><Users className="w-5 h-5 text-primary" />Dettaglio utente</DialogTitle>
            <DialogDescription>Libri pubblicati e storico scambi dell'utente selezionato.</DialogDescription>
          </DialogHeader>
          {detailLoading || !detail ? (
            <div className="py-16 flex items-center justify-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="space-y-6">
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-lg font-serif font-semibold">{detail.user.name}</span>
                  {detail.user.role === "admin" && <Badge className="bg-primary text-primary-foreground">Admin</Badge>}
                  {detail.user.rating > 0 && <span className="text-sm text-amber-600 flex items-center gap-0.5"><Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />{detail.user.rating.toFixed(1)}</span>}
                </div>
                <div className="mt-2 grid sm:grid-cols-2 gap-1.5 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /><a href={`mailto:${detail.user.email}`} className="hover:text-primary break-all">{detail.user.email}</a></span>
                  {detail.user.comune && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{detail.user.comune}</span>}
                  <span>Iscritto il {detail.user.created_at ? new Date(detail.user.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "—"}</span>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4" />Libri pubblicati <span className="text-muted-foreground font-normal">({detail.books.length})</span></h3>
                {detail.books.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun libro pubblicato.</p>
                ) : (
                  <div className="space-y-2">
                    {detail.books.map((b) => (
                      <div key={b.id} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
                        <div className="min-w-0">
                          <Link to={`/libro/${b.id}`} className="font-medium hover:text-primary line-clamp-1">{b.title}</Link>
                          <p className="text-xs text-muted-foreground line-clamp-1">{b.author}{b.is_hidden ? " · nascosto" : ""}</p>
                        </div>
                        <Badge variant="outline" className={statusClass(b.status)}>{STATUS_LABEL[b.status] || b.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2"><ArrowLeftRight className="w-4 h-4" />Storico scambi <span className="text-muted-foreground font-normal">({detail.proposals_sent.length + detail.proposals_received.length})</span></h3>
                {detail.proposals_sent.length === 0 && detail.proposals_received.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessuno scambio.</p>
                ) : (
                  <div className="space-y-2">
                    {[...detail.proposals_sent.map((p) => ({ ...p, dir: "sent" })), ...detail.proposals_received.map((p) => ({ ...p, dir: "received" }))].map((p) => (
                      <div key={p.id} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
                        <div className="min-w-0 flex items-center gap-2">
                          {p.dir === "sent" ? <Send className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <Inbox className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                          <span className="text-sm line-clamp-1">
                            <span className="text-muted-foreground">{p.dir === "sent" ? "Inviata ·" : "Ricevuta ·"}</span> «{p.book?.title || "libro rimosso"}»
                          </span>
                        </div>
                        <Badge variant="outline" className={statusClass(p.status)}>{STATUS_LABEL[p.status] || p.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {detail.user.role !== "admin" && (
                <div className="pt-2 border-t border-border flex justify-end">
                  <Button variant="outline" data-testid="user-detail-delete" onClick={() => setDeleteTarget(detail.user)} className="text-destructive hover:text-destructive"><Trash2 className="w-4 h-4 mr-2" />Elimina utente</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione è irreversibile. Verranno eliminati definitivamente l'utente e tutti i suoi contenuti: libri pubblicati, proposte inviate e ricevute, recensioni, lista desideri, avvisi e follow.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-user-confirm" onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={purgeOpen} onOpenChange={(o) => { if (!o) setPurgeOpen(false); }}>
        <AlertDialogContent data-testid="purge-demo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere i libri di esempio?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati tutti gli annunci di esempio (badge "Esempio") dal catalogo. I libri reali pubblicati dagli utenti non saranno toccati. L'operazione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purge-demo-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="purge-demo-confirm" onClick={(e) => { e.preventDefault(); purgeDemo(); }} disabled={purging} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {purging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}Rimuovi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
